import { BoardParams } from '@/hooks/apiHooks';
import { BOARD_URL } from '@/constant/apiList';
import process from 'process';
import { PAGE_URL } from '@/constant/url';
import { NATIVE_VIEW_APPROVAL_TYPES } from '@/constant';
import { isNativeApprovalType } from '@/utils/approval/approvalAppTab';

const boardParamMap = (key: keyof BoardParams) => {
  switch (key) {
    case 'boardNo':
      return 'n';
    case 'category':
      return 'c';
    case 'documentNo':
      return 'd';
  }
};
export const makeBoardUrl = (obj: BoardParams) => {
  const newObj = Object.entries(obj).map(([k, v]) => [
    boardParamMap(k as keyof BoardParams),
    v,
  ]);
  const params = new URLSearchParams(Object.fromEntries(newObj));
  return `${BOARD_URL}?${params.toString()}`;
};

export type BaseValue = string | number | boolean;

export const queryBuilder = (
  payload?: Record<string, BaseValue | BaseValue[]>,
  options?: {
    includeQuestionMark?: boolean;
    filterNull?: boolean;
    arraySeparator?: string; // 배열값 구분자도 선택 지원
  },
): string => {
  if (!payload) return '';

  const {
    includeQuestionMark = false,
    filterNull = true,
    arraySeparator = ',',
  } = options ?? {};

  const queryString = Object.entries(payload)
    .filter(([_, value]) =>
      filterNull ? value !== null && value !== undefined : value !== undefined,
    )
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(
          Array.isArray(value) ? value.join(arraySeparator) : String(value),
        )}`,
    )
    .join('&');

  if (!queryString) return '';
  return includeQuestionMark ? `?${queryString}` : queryString;
};

/**
 * 레거시 인트라넷 문서 URL.
 *
 * portalApiKey: 앱 웹뷰처럼 레거시 쪽 쿠키가 없는 상태로 열리는 경우에만 호출부에서
 * `jwt_access_token` 쿠키 값을 넘긴다. 일반 브라우저 iframe 임베드에는 넘기지 말 것 —
 * 히스토리·Referer 에 JWT 가 남는다.
 */
export const getIntranetDocUrl = (
  docId: string | undefined | null,
  isModal: boolean,
  isCreate?: boolean,
  portalApiKey?: string,
) => {
  const url = `${process.env.NEXT_PUBLIC_IFRAME_URL}/intranet/approval/${
    isCreate ? 'create?apv_type=' : 'v?doc='
  }${docId || ''}${isModal ? `&mode=modal` : ''}`;

  // 두 분기 모두 쿼리(`?apv_type=` / `?doc=`)로 시작하므로 구분자는 `&` 고정
  if (!portalApiKey) return url;

  return `${url}&portalApiKey=${encodeURIComponent(portalApiKey)}`;
};

export const getApprovalDocUrl = (docId: number | string, isCreate?: boolean) =>
  `${process.env.NEXT_PUBLIC_ORIGIN_URL}/approval/viewer?doc=${docId || ''}${
    isCreate ? '&create=true' : ''
  }`;

/** 포탈 v2 작성 페이지 URL — RN 웹뷰 postMessage 로 넘겨야 해서 절대경로 */
export const getApprovalCreateUrl = (apvType: number) =>
  `${process.env.NEXT_PUBLIC_ORIGIN_URL}/approval/create?apv_type=${apvType}`;

/**
 * 취소 품의 작성 URL — 원 품의를 `doc` 으로 넘김. 취소 양식이 V2 이관(NATIVE)이면 포탈 작성 페이지,
 * 아니면 레거시 create. 진입점(조회 「품의 취소」·마이페이지 근태)이 전부 이걸 써야 분기가 한곳에 남음
 */
export const getApprovalCancelCreateUrl = (formType: number, docId: string) =>
  isNativeApprovalType(formType)
    ? `${getApprovalCreateUrl(formType)}&doc=${docId}`
    : `${process.env.NEXT_PUBLIC_IFRAME_URL}/intranet/approval/create?apv_type=${formType}&doc=${docId}`;

/**
 * 인테리어 보고서 → 품의 작성 URL(`analysisId`). V2 이관(NATIVE) 양식이면 포탈 작성 페이지, 아니면 레거시 create.
 * 앱 채널은 URL 호스트로 정해지므로 호출부는 sendApprovalDocToApp 으로 넘길 것
 */
export const getApprovalAnalysisCreateUrl = (
  formType: number,
  analysisId: string,
) =>
  isNativeApprovalType(formType)
    ? `${getApprovalCreateUrl(formType)}&analysisId=${analysisId}`
    : `${process.env.NEXT_PUBLIC_IFRAME_URL}/intranet/approval/create?apv_type=${formType}&analysisId=${analysisId}`;

/**
 * 양식 코드로 조회 URL 결정 — v2 이관 유형은 포탈 뷰어, 그 외는 레거시.
 *
 * portalApiKey 는 레거시 분기에만 붙는다. 포탈 URL 은 toAppWebviewUrl 이 붙여줌.
 */
export const getApprovalViewUrlByType = (
  docId: string,
  apvType: number,
  portalApiKey?: string,
) =>
  NATIVE_VIEW_APPROVAL_TYPES.includes(apvType)
    ? getApprovalDocUrl(docId)
    : getIntranetDocUrl(docId, false, false, portalApiKey);

/** 포탈 편집 페이지 URL — /approval/edit?doc={docId} */
export const getApprovalEditUrl = (docId: string) =>
  `/approval/edit?doc=${docId}`;

export const sanitizeQuery = (
  query: Record<string, string | string[] | undefined>,
): Record<string, BaseValue | BaseValue[]> => {
  return Object.fromEntries(
    Object.entries(query)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, value!]),
  );
};

export const cleanUpQuery = (
  query: Record<string, unknown>,
  keys: string[],
) => {
  keys.forEach((key) => delete query[key]);
  return query;
};

export const getOrganizationUrl = (
  organizationCode: string | null,
  queries: Record<string, BaseValue | BaseValue[]>,
) => {
  let baseUrl = process.env.NEXT_PUBLIC_ORIGIN_URL + PAGE_URL.ORGANIZATION;

  if (organizationCode) baseUrl += '/' + organizationCode;

  return baseUrl + queryBuilder(queries, { includeQuestionMark: true });
};
